/**
 * CustomerGrowthMetrics
 *
 * Customer & Growth Metrics section of the analytics dashboard.
 * Displays Paying Customers, LTV, Churn Rate, and Customer Retention.
 */
import React from 'react';
import { Users, TrendingUp, UserX, Activity } from 'lucide-react';
import AnalyticsTooltip from './AnalyticsTooltip';

const CustomerGrowthMetrics = ({ analyticsData, formatCurrency, formatPercentage }) => {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer & Growth Metrics</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
        {/* Total Paying Customers */}
        <AnalyticsTooltip content="Paying Customers - Number of users with active subscriptions (subscriptionStatus = 'active'). These customers are currently generating revenue and contributing to MRR." id="paying-customers-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mr-3">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Paying Customers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {(analyticsData.summary?.payingCustomers || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  of {(analyticsData.summary?.totalUsers || 0).toLocaleString()} total users
                </p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>

        {/* Average LTV */}
        <AnalyticsTooltip content="Customer Lifetime Value - Average total revenue generated per customer who has ever paid (including both active and churned customers). Calculated as total revenue ÷ all customers who have ever paid." id="ltv-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg mr-3">
                <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Average LTV</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(analyticsData.summary?.averageLTV || 0)}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  Per customer lifetime
                </p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>

        {/* Churn Rate */}
        <AnalyticsTooltip content="Churn Rate - Percentage of customers who have cancelled their subscriptions. Calculated as former subscribers divided by total customers who have ever paid." id="churn-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg mr-3">
                <UserX className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Churn Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatPercentage(analyticsData.summary?.churnRate)}
                </p>
                <p className="text-xs text-red-600 dark:text-red-400">
                  {(analyticsData.summary?.churnedUsers || 0).toLocaleString()} churned users
                </p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>

        {/* Customer Retention */}
        <AnalyticsTooltip content="Customer Retention Rate - Percentage of customers who remain active and continue paying. Calculated as 100% minus churn rate. Higher retention indicates better product satisfaction." id="retention-tooltip">
          <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-help">
            <div className="flex items-center">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg mr-3">
                <Activity className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">Customer Retention</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {(100 - (analyticsData.summary?.churnRate || 0)).toFixed(1)}%
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  {analyticsData.summary?.payingCustomers || 0} active customers
                </p>
              </div>
            </div>
          </div>
        </AnalyticsTooltip>
      </div>
    </div>
  );
};

export default CustomerGrowthMetrics;


